import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getMediaElement, videoStatisticsFinish, videoStatisticsGetPosition, videoStatisticsStart, videoStatisticsWatch } from '../../Utilities/api';
import VidStackPlayer from './Players/VidStackPlayer';
import { debounce } from '../Utils/useDebounce';
import { isIOS } from '../../Utilities/isIos';
import { fetchVideoAccessToken } from '../../Utilities/videoServiceClient';
const VideoPlayer = (props) => {
    const { id, data, controls, playing, muted, preview, responsive, gaAttribute } = props;
    const [userVideoId, setUserVideoId] = useState('');
    const [initialTime, setInitialTime] = useState(0);
    const [isLoadingPosition, setIsLoadingPosition] = useState(true);
    const [isPortraitSource, setIsPortraitSource] = useState(false);
    // When the video-service-playback flag is on for this user, the token
    // endpoint returns a signed HLS URL keyed on the MediaElement id; otherwise
    // it 404s and we keep the legacy MinIO MP4 path. Resolved before first paint
    // so the player mounts once with the right source.
    const [videoServiceUrl, setVideoServiceUrl] = useState(null);
    const handleSourceDimensions = useCallback((width, height) => {
        setIsPortraitSource(height > width);
    }, []);
    useEffect(() => {
        const initialiseSource = async () => {
            if (!props.disableTracking && (data.type === 'video' || data.type === 'broadcast')) {
                const position = await videoStatisticsGetPosition(id);
                setInitialTime(position);
            }
            if (data.type === 'video' && id) {
                const access = await fetchVideoAccessToken(String(id));
                if (access?.videoUrl) {
                    setVideoServiceUrl(access.videoUrl);
                }
            }
            setIsLoadingPosition(false);
        };
        initialiseSource();
    }, [id, data.type, props.disableTracking]);
    const gaAttributes = (gaAttribute ? gaAttribute.split(' ') : []).map(element => {
        const split = element.split('=');
        return {
            identifier: split[0],
            label: split[1],
        };
    });
    const attributes = {};
    gaAttributes?.forEach(element => {
        if (element.identifier && element.label) {
            attributes['data-' + element.identifier] = element.label;
        }
    });
    let url = '';
    let customPreview = preview;
    if (data.type === 'youtube') {
        url = `https://youtube.com/watch?v=${data.video}`;
        customPreview = true;
    }
    const config = {
        file: {},
    };
    if (data.type === 'video') {
        if (videoServiceUrl) {
            url = videoServiceUrl;
            config.file['forceHLS'] = true;
        }
        else {
            url = getMediaElement(data.video, 'v');
            config.file['forceVideo'] = true;
        }
        let thumbnail = data.thumbnail;
        if (props.thumbnailWidth || props.thumbnailHeight) {
            if (!thumbnail.includes('?')) {
                thumbnail += '?';
            }
            const searchParams = {};
            if (props.thumbnailWidth) {
                searchParams['width'] = props.thumbnailWidth;
            }
            if (props.thumbnailHeight) {
                searchParams['height'] = props.thumbnailHeight;
            }
            thumbnail += new URLSearchParams(searchParams);
        }
        customPreview = thumbnail;
    }
    if (props.forVideoSeries === true) {
        url = props.fetchVideoSeriesVideo || '';
    }
    let activatePlaying = playing;
    let controlElements = controls;
    if (data.type === 'broadcast') {
        url = data.video;
        if (data.isLive) {
            // iOS comes with native HLS support, so we don't need to use the HLS.js wrapper
            config.file['forceHLS'] = !isIOS();
            activatePlaying = true;
            controlElements = true;
            customPreview = data.light;
        }
        else {
            config.file['forceVideo'] = true;
        }
    }
    const debouncedVideoStatisticsWatch = useCallback(debounce((userVideoId, id, playedSeconds) => {
        videoStatisticsWatch(userVideoId, id, playedSeconds);
    }, 500), []);
    const onEnded = () => {
        if (typeof props.redirectToOnFinished === 'undefined') {
            return () => { }; // eslint-disable-line
        }
        if (typeof props.redirectToOnFinished === 'function') {
            return props.redirectToOnFinished;
        }
        return (() => window.location.href = String(props.redirectToOnFinished));
    };
    const additionalConfig = {
        url,
        controls: controlElements,
        playing: activatePlaying,
        muted,
        volume: 1,
        light: customPreview,
        ...attributes,
        onEnded: onEnded(),
        chapters: data.chapters,
    };
    const onProgress = (stats) => {
        if (props.disableTracking) {
            return;
        }
        if (data.type === 'broadcast' && window.hasOwnProperty('plausible')) {
            // @ts-ignore
            window.plausible('aktive-pause-started');
        }
        if (userVideoId === '') {
            return;
        }
        if (stats.playedSeconds > 3 && ((Math.round(stats.playedSeconds) % 3 === 0 && stats.played < 0.98))) {
            debouncedVideoStatisticsWatch(userVideoId, id, stats.playedSeconds);
        }
    };
    const onFinish = () => {
        if (props.disableTracking || userVideoId === '') {
            return;
        }
        videoStatisticsFinish(userVideoId, id);
    };
    const onStarted = () => {
        if (props.disableTracking) {
            return;
        }
        videoStatisticsStart(id).then((v) => setUserVideoId(v));
    };
    // if (false) {
    //     return <VidStackPlayer url={url} type={'YOUTUBE'} videoTitle={'Test-Title'} />
    // }
    const vidstackPlayer = <VidStackPlayer playerConfig={additionalConfig} sourceConfig={config} onStart={props.onStart || onStarted} onProgress={props.onProgress || onProgress} onEnded={props.onFinish || onFinish} disableSeek={props.disableSeek} height={props.height} width={props.width} initialTime={initialTime} isLoadingPosition={isLoadingPosition} onSourceDimensions={handleSourceDimensions}/>;
    // Portrait videos uploaded by trainers should not eat the whole 1920px monitor — cap their width
    // and centre them so the result feels intentional rather than a UI bug.
    const portraitWrapperClass = isPortraitSource ? 'mx-auto max-w-[480px]' : '';
    const baseWrapperClass = props.fullscreen ? '' : 'mx-4';
    const wrapperClass = [baseWrapperClass, portraitWrapperClass].filter(Boolean).join(' ');
    return <div className={wrapperClass} style={{ margin: props.withMargin ? '45px' : '0', width: props.width ?? '', height: props.height ?? '' }}>
        {vidstackPlayer}
    </div>;
};
VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;
